import { formatDateInputValue } from '@/utils/helpers';
import {
  ProductionReportMeasurementEntryEnum,
  type MaterialUnitQuantities,
  type ProductionOrder,
  type ProductionPeriod,
  type ProductionReportMeasurement,
  type ProductionReportMeasurementInput,
  type TagValueData,
} from './types';
import type {
  Material,
  MaterialComponent,
  MaterialRouting,
} from '@/features/match-maker/utils/match-maker.types';

export const shiftDateByDays = (date: string, offset: number): string => {
  const [year, month, day] = date.split('-').map(Number);
  const shiftedDate = new Date(year, month - 1, day);
  shiftedDate.setDate(shiftedDate.getDate() + offset);

  return formatDateInputValue(shiftedDate);
};

export const getEarlierDate = (leftDate: string, rightDate: string): string => {
  return leftDate <= rightDate ? leftDate : rightDate;
};

export const formatPeriodRange = (
  start: string,
  end: string,
  onlyTime = false
): string => {
  if (!start || !end) return '-';

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return '-';
  }

  const dateAndStartTime = new Intl.DateTimeFormat('en-GB', {
    day: onlyTime ? undefined : '2-digit',
    month: onlyTime ? undefined : 'short',
    year: onlyTime ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(startDate);

  const endTime = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  }).format(endDate);

  return `${dateAndStartTime} → ${endTime}`;
};

export const formatDuration = (start: string, end: string): string => {
  if (!start || !end) return '-';
  const diffMs = new Date(end).getTime() - new Date(start).getTime();
  const totalMinutes = Math.round(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

export const toTimeInputValue = (value?: string | null): string => {
  if (!value) return '';

  const rawValue = String(value).trim();

  if (/^\d{2}:\d{2}$/.test(rawValue)) {
    return rawValue;
  }

  const dateTimeMatch = rawValue.match(
    /(?:T|\s)(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?/
  );
  if (dateTimeMatch?.[1]) {
    return dateTimeMatch[1];
  }

  const timeMatch = rawValue.match(/\b(\d{2}:\d{2})(?::\d{2})?\b/);
  return timeMatch?.[1] ?? '';
};

export const convertFromMeasurementToInput = (
  measurement: ProductionReportMeasurement
): ProductionReportMeasurementInput => {
  return {
    quantity: measurement.quantity ?? null,
    unit: measurement.unit as ProductionReportMeasurementInput['unit'],
    entry_type: measurement.entry_type,
    tag_id: measurement.tag_id,
    material_id: measurement.material_id,
    routing_id: measurement.routing_id,
    component_id: measurement.component_id,
  };
};

type FetchInitialMeasurementsParams = {
  materialDetail: Material;
  tagValues: Record<string, TagValueData>;
  materialUnits: Record<string, string>;
};

export const fetchInitialMeasurements = ({
  materialDetail,
  tagValues,
  materialUnits,
}: FetchInitialMeasurementsParams): Record<
  string,
  ProductionReportMeasurementInput
> => {
  const initialMeasurements: Record<string, ProductionReportMeasurementInput> =
    {};

  // Logic to initialize measurements for material without tags
  initialMeasurements[materialDetail.id] = {
    material_id: materialDetail.id,
    routing_id: null,
    component_id: null,
    quantity: 0,
    unit: materialUnits[materialDetail.material_code] || '',
    entry_type: ProductionReportMeasurementEntryEnum.MANUAL,
    tag_id: null,
  };
  const primaryTag = materialDetail.tags?.find(tag => tag.priority === 1);

  if (primaryTag) {
    initialMeasurements[materialDetail.id] = {
      ...initialMeasurements[materialDetail.id],
      quantity: tagValues[primaryTag.id]?.value ?? 0,
      unit:
        primaryTag.unit || materialUnits[materialDetail.material_code] || '',
      entry_type: ProductionReportMeasurementEntryEnum.TAG,
      tag_id: primaryTag.id,
    };
  }

  if ((materialDetail.components || []).length > 0) {
    (materialDetail.components || []).forEach(component => {
      initialMeasurements[component.id] = {
        material_id: materialDetail.id,
        routing_id: null,
        component_id: component.id,
        quantity: 0,
        unit: materialUnits[component.component] || '',
        entry_type: ProductionReportMeasurementEntryEnum.MANUAL,
        tag_id: null,
      };
      const primaryTag = component.tags?.find(tag => tag.priority === 1);
      if (primaryTag) {
        initialMeasurements[component.id] = {
          ...initialMeasurements[component.id],
          quantity: tagValues[primaryTag.id]?.value ?? 0,
          entry_type: ProductionReportMeasurementEntryEnum.TAG,
          tag_id: primaryTag.id,
          unit: primaryTag.unit || materialUnits[component.component] || '',
        };
      }
    });
  }
  if ((materialDetail.routings || []).length > 0) {
    (materialDetail.routings || []).forEach(routing => {
      initialMeasurements[routing.id] = {
        material_id: materialDetail.id,
        routing_id: routing.id,
        component_id: null,
        quantity: 0,
        unit: materialUnits[routing.workcenter] || '',
        entry_type: ProductionReportMeasurementEntryEnum.MANUAL,
        tag_id: null,
      };

      const primaryTag = routing.tags?.find(tag => tag.priority === 1);
      if (primaryTag) {
        initialMeasurements[routing.id] = {
          ...initialMeasurements[routing.id],
          quantity: tagValues[primaryTag.id]?.value ?? 0,
          entry_type: ProductionReportMeasurementEntryEnum.TAG,
          tag_id: primaryTag.id,
          unit: primaryTag.unit || materialUnits[routing.workcenter] || '',
        };
      }
    });
  }
  return initialMeasurements;
};

export const convertMinutesToHours = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (minutes) {
    return `${hours}:${minutes.toString().padStart(2, '0')}h`;
  }
  return `${hours}h`;
};

export const findTotalMinutesOfProductionPeriods = (
  productionPeriodItems: ProductionPeriod[]
): number => {
  if (!productionPeriodItems || productionPeriodItems.length === 0) return 0;
  return productionPeriodItems.reduce((acc, period) => {
    const startTime = new Date(period.start_time);
    const endTime = new Date(period.end_time);
    const startHour = startTime.getUTCHours();
    const startMinute = startTime.getUTCMinutes();
    const endHour = endTime.getUTCHours();
    const endMinute = endTime.getUTCMinutes();
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    return acc + (endMinutes - startMinutes);
  }, 0);
};

export const fetchMaterialUnitsFromProductionOrder = (
  productionOrders: ProductionOrder
): Record<string, string> => {
  const materialUnits: Record<string, string> = {};
  const untilityUnits: Record<string, string> = {};
  const order = productionOrders.productionOrder;
  if (order && order.material?.code) {
    // Fetch output material unit
    materialUnits[order.material.code] =
      order.alternativeQuantity.quantityUnit ?? '';
    // Fetch Utility material's units if available
    (order.productionOrderOperations || []).forEach(operation => {
      if (operation.capacityWorkCenter.code) {
        const activity = (operation.activities || []).length
          ? operation.activities[0]
          : null;
        untilityUnits[operation.capacityWorkCenter.code] = activity?.unit ?? '';
      }
    });

    // Fetch Input's and ByProduct's units if available
    const inputAndByProductMaterials = (
      order.productionOrderOperations || []
    ).reduce(
      (acc, cur) => {
        const materialItemsObject: Record<string, string> = Object.fromEntries(
          cur.materialItems.map(mat => [mat.material.code, mat.quantityUnit])
        );
        return { ...acc, ...materialItemsObject };
      },
      {} as Record<string, string>
    );
    return {
      ...materialUnits,
      ...untilityUnits,
      ...inputAndByProductMaterials,
    };
  }
  return {};
};

export const fetchDefaultUnitFromMaterial = (
  material: Material | MaterialRouting | MaterialComponent,
  materialUnits: Record<string, string>
): string => {
  if (!material) return '';
  if (Object.hasOwn(material || {}, 'workcenter')) {
    return materialUnits[(material as MaterialRouting).workcenter] || '';
  } else if (Object.hasOwn(material || {}, 'component')) {
    return materialUnits[(material as MaterialComponent).component] || '';
  } else {
    return materialUnits[(material as Material).material_code] || '';
  }
};

type SetInitialMaterialUnitsParams = {
  materialDetail: Material;
};
export const setInitialMaterialUnits = ({
  materialDetail,
}: SetInitialMaterialUnitsParams): MaterialUnitQuantities => {
  if (!materialDetail) {
    return {};
  }
  // Output material
  const materialUnitQuantities: MaterialUnitQuantities = {};
  materialUnitQuantities[materialDetail.id] = {
    quantity: materialDetail.base_quantity ?? '',
    unit: materialDetail.base_unit ?? '',
  };
  // Components
  (materialDetail.components || []).forEach(component => {
    materialUnitQuantities[component.id] = {
      quantity: component.quantity ?? '',
      unit: component.unit ?? '',
    };
  });
  // Routings
  (materialDetail.routings || []).forEach(routing => {
    materialUnitQuantities[routing.id] = {
      quantity: routing.activity_value ?? '',
      unit: routing.activity_unit ?? '',
    };
  });
  return materialUnitQuantities;
};
